import { Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { ManualActivation } from '@lib/activation';
import { PROFILE_TYPE } from '@lib/core/enum';
import { EnvironmentService } from '@lib/core/environment';
import { BaseProfile, BaseUser } from '@lib/core/model';
import { ExportConfiguration, Id, Uuid } from '@lib/core/type';
import { AuthHttpClient } from '@proget-shared/auth-http-client';
import { PagingList } from '@proget-shared/grid/grid-control';
import { ResponseErrorFactory } from '@proget-shared/helper/response-error';
import { catchError, map, Observable, of } from 'rxjs';

import { ATTRIBUTES_DISPLAY_NAME } from './const';
import { AttributeType } from './enum';
import { BackupMapper, DeviceListItemMapper, UserMapper } from './mapper';
import { Attribute, DeviceBackup, DeviceListItem, Ldap, User } from './model';
import { Certificate } from './model/certificate';
import { UserCertificate } from './model/user-certificate';

@Injectable({
  providedIn: 'root',
})
export class UserCardRepository {
  private readonly httpClient = inject(AuthHttpClient);
  private readonly environment = inject(EnvironmentService);

  public getUser(uuid: Uuid, persistRequest = false): Observable<User> {
    return this.httpClient
      .get(
        `${this.environment.apiUrl}/user/${uuid}`,
        { params: { persist: persistRequest.toString() } }
      )
      .pipe(map((userData: any) => UserMapper.map(userData)));
  }

  public getSelf(persistRequest = false): Observable<User> {
    return this.httpClient
      .get(
        `${this.environment.apiUrl}/me`,
        { params: { persist: persistRequest.toString() } }
      )
      .pipe(map((userData: any) => UserMapper.map(userData)));
  }

  public createActivation(id: Uuid, activationTemplateId: string, sendEmail: boolean): Observable<ManualActivation> {
    return this.httpClient
      .post(`${this.environment.apiUrl}/user/${id}/activation`, { activationTemplateId, sendEmail })
      .pipe(
        map((data: any): ManualActivation => new ManualActivation(
          data.message,
          data.qrcode,
          data.serverHostname,
          data.userDisplayName,
          data.activation
        )),
        catchError((error) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public getActivation(id: Uuid): Observable<ManualActivation> {
    return this.httpClient
      .get(`${this.environment.apiUrl}/user/${id}/activation`)
      .pipe(
        map(
          (data: any): ManualActivation => new ManualActivation(
            data.message,
            data.qrcode,
            data.serverHostname,
            data.userDisplayName,
            data.activation
          )
        )
      );
  }

  public deleteActivation(id: Uuid): Observable<void> {
    return this.httpClient
      .delete<void>(`${this.environment.apiUrl}/user/${id}/activation`)
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public sendMail(id: Uuid, message: string): Observable<void> {
    return this.httpClient
      .post<void>(`${this.environment.apiUrl}/user/mail`, { ids: [id], message })
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public markAsVip(id: Uuid): Observable<any> {
    return this.httpClient
      .post(`${this.environment.apiUrl}/user/${id}/vip`, {})
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public unmarkVip(id: Uuid): Observable<any> {
    return this.httpClient
      .delete(`${this.environment.apiUrl}/user/${id}/vip`)
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public setLabels(id: Uuid, labels: Uuid[]): Observable<any> {
    return this.httpClient
      .post(`${this.environment.apiUrl}/user/${id}/label/set`, { labels })
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public resetTwoFactor(id: Uuid): Observable<any> {
    return this.httpClient
      .post(`${this.environment.apiUrl}/user/${id}/reset-2fa`, null)
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public lock(id: Uuid): Observable<any> {
    return this.httpClient
      .post(`${this.environment.apiUrl}/user/${id}/lock`, {})
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public unlock(id: Uuid): Observable<any> {
    return this.httpClient
      .delete(`${this.environment.apiUrl}/user/${id}/lock`)
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public assignSuperAdmin(id: Uuid): Observable<void> {
    return this.httpClient
      .post<void>(`${this.environment.apiUrl}/user/${id}/super-admin`, null)
      .pipe(
        catchError((error) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public unassignSuperAdmin(id: Uuid): Observable<void> {
    return this.httpClient
      .delete<void>(`${this.environment.apiUrl}/user/${id}/super-admin`)
      .pipe(
        catchError((error) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public updateUser(
    id: Uuid,
    firstName: string,
    lastName: string,
    email: string,
    username: string
  ): Observable<void> {
    return this.httpClient
      .put<void>(`${this.environment.apiUrl}/user/${id}`, {
        firstName,
        lastName,
        email,
        username,
      })
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public resetPassword(userId: Uuid, emailLanguage: string): Observable<{ url: string }> {
    return this.httpClient
      .post<{ url: string }>(`${this.environment.apiUrl}/user/${userId}/reset-password`, {
        emailLanguage,
      })
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public changePassword(userId: Uuid, currentPassword: string, password: string): Observable<any> {
    return this.httpClient
      .put(`${this.environment.apiUrl}/user/${userId}/password`, {
        currentPassword,
        password,
      })
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public generatePin(id: Uuid): Observable<string> {
    return this.httpClient
      .post(`${this.environment.apiUrl}/user/${id}/pin`, null)
      .pipe(
        map(({ pin }: { pin: string }) => pin),
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public delete(uuid: Uuid): Observable<void> {
    return this.httpClient
      .delete<void>(`${this.environment.apiUrl}/user/${uuid}`)
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public getAllUserDevices(userId: Uuid, params?: Params): Observable<PagingList<DeviceListItem>> {
    return this.httpClient
      .get(`${this.environment.apiUrl}/user/${userId}/device`, {
        params,
      })
      .pipe(
        map(
          (responseJson: any): PagingList<DeviceListItem> => new PagingList<DeviceListItem>(
            responseJson.items.map(DeviceListItemMapper.map),
            responseJson.limit,
            responseJson.offset,
            responseJson.total
          )
        )
      );
  }

  public deleteDevice(uuid: Uuid): Observable<any> {
    return this.httpClient
      .delete(`${this.environment.apiUrl}/device/${uuid}`)
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public refreshDevice(id: Uuid): Observable<void> {
    return this.httpClient.post<void>(
      `${this.environment.apiUrl}/device/${id}/status`,
      null
    );
  }

  public exportDeviceData(configuration: ExportConfiguration): Observable<any> {
    return this.httpClient
      .post(`${this.environment.apiUrl}/device/export`, configuration)
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public generateDeviceReport(
    deviceIds: Id[],
    foreground: boolean,
    foregroundFrom: string,
    foregroundTo: string,
    mobile: boolean,
    mobileFrom: string,
    mobileTo: string,
    network: boolean,
    networkFrom: string,
    networkTo: string,
    storage: boolean
  ): Observable<any> {
    return this.httpClient
      .post(`${this.environment.apiUrl}/usage/report`, {
        deviceIds,
        foreground,
        foregroundFrom,
        foregroundTo,
        mobile,
        mobileFrom,
        mobileTo,
        network,
        networkFrom,
        networkTo,
        storage,
      })
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public sendDeviceMessage({
    id,
    message,
    audible,
    vibrate,
    dialog,
  }: {
    id: Uuid[];
    message: string;
    audible: boolean;
    vibrate: boolean;
    dialog: { timeToRead: number } | undefined;
  }): Observable<any> {
    return this.httpClient
      .post(`${this.environment.apiUrl}/device/message`, { id, message, audible, vibrate, dialog })
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public sendDeviceAlarmMessage({
    id,
    message,
    vibrate,
  }: {
    id: Id[];
    message: string;
    vibrate: boolean;
  }): Observable<any> {
    return this.httpClient
      .post(`${this.environment.apiUrl}/device/alarm`, {
        id,
        message,
        vibrate,
      })
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public resetDevicePassword(uuid: Uuid): Observable<any> {
    return this.httpClient
      .post(`${this.environment.apiUrl}/device/${uuid}/reset-password`, null)
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public getAllUserCertificates(subjectId: Uuid, params?: Params): Observable<PagingList<UserCertificate>> {
    return this.httpClient
      .get<any>(
        `${this.environment.apiUrl}/settings/certificate/issued`,
        { params: Object.assign(params, { subjectId, generated: 'true' }) }
      )
      .pipe(
        map(
          (data) => new PagingList(
            data.items.map((item) => new UserCertificate(
              item.id,
              item.purpose,
              this.mapCertificate(item.certificate),
              item.subject.type === 'user' ? new BaseUser(
                null,
                item.subject.uuid,
                item.subject.displayName,
                '',
                ''
              ) : null,
              item.certificateProfile ? new BaseProfile(
                item.certificateProfile.id,
                item.certificateProfile.uuid,
                item.certificateProfile.name,
                PROFILE_TYPE.PROFILE_CERT
              ) : null,
              item.signingCertificateName,
              item.status,
              item.certificateProfile?.type
            )),
            data.limit,
            data.offset,
            data.total
          )
        )
      );
  }

  public getAllBackups(userId: Uuid, params?: Params): Observable<PagingList<DeviceBackup>> {
    return this.httpClient
      .get(`${this.environment.apiUrl}/user/${userId}/backup`, {
        params,
      })
      .pipe(
        map(
          (data: any): PagingList<DeviceBackup> => new PagingList<DeviceBackup>(
            data.items.map((item: any) => BackupMapper.map(item)),
            data.limit,
            data.offset,
            data.total
          )
        )
      );
  }

  public deleteBackup(id: Uuid): Observable<void> {
    return this.httpClient
      .delete<void>(`${this.environment.apiUrl}/backup/${id}`)
      .pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public generateBackupQrCode(id: Uuid): Observable<string> {
    return this.httpClient
      .post<{ qrcode: string }>(`${this.environment.apiUrl}/backup/generate`, { userUuid: id })
      .pipe(map(({ qrcode }) => qrcode));
  }

  public renewUserCertificate(id: Uuid): Observable<void> {
    return this.httpClient.post<void>(`${this.environment.apiUrl}/settings/certificate/issued/${id}/renew`, {})
      .pipe(
        catchError((error) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public revokeUserCertificate(id: Uuid): Observable<void> {
    return this.httpClient.post<void>(`${this.environment.apiUrl}/settings/certificate/issued/${id}/revoke`, {})
      .pipe(
        catchError((error) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  public getAttributes(id: Uuid): Observable<Attribute[]> {
    return this.httpClient
      .get(`${this.environment.apiUrl}/user/${id}/attributes`)
      .pipe(
        map(
          (responseData: any): Attribute[] => responseData.map((data: any) => {
            const tempDisplayName = data.displayName.toLowerCase().replace(/ /g, '_');

            return new Attribute(
              data.displayName,
              tempDisplayName && ATTRIBUTES_DISPLAY_NAME.includes(tempDisplayName)
                ? `library.user_card.attributes.display_name.option.${tempDisplayName}` : '',
              data.type === AttributeType.LDAP ? `{UserAttr.${data.key}}` : `{${data.key}}`,
              data.key,
              data.value,
              data.type,
              data.source && data.type === AttributeType.LDAP
                ? new Ldap(data.source.id, data.source.name)
                : null,
              data.editable
            );
          })
        ),
        catchError(() => of([]))
      );
  }

  public updateAttributeValue(id: Uuid, name: string, value: string): Observable<any> {
    return this.httpClient
      .put(`${this.environment.apiUrl}/user/${id}/attributes`, { name, value }).pipe(
        catchError((error: any) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  private mapCertificate(data: any | null): Certificate | null {
    return data
      ? new Certificate(
        data.selfSigned,
        data.subject,
        Object.keys(data.extra || {}).length ? data.extra : null,
        data.issuer,
        data.validity,
        data.valid
      )
      : null;
  }
}
